import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Button,
  Form,
  Table,
  Alert,
  Spinner,
  Badge,
  InputGroup,
} from "react-bootstrap";
import Select from "react-select";
import api from "../../../axiosConfig";
import "../../user/tabs/checksheet.css";

const FREQ_OPTIONS = [
  { value: "H", label: "H (Harian)" },
  { value: "M", label: "M (Mingguan)" },
  { value: "B", label: "B (Bulanan)" },
];

let _k = 0;
const nextKey = () => `k${++_k}`;

const ChecksheetTab = () => {
  const [machines, setMachines] = useState([]);
  const [allTools, setAllTools] = useState([]);
  const [search, setSearch] = useState("");
  const [sectorFilter, setSectorFilter] = useState(""); // "" = semua sektor

  const [view, setView] = useState("list"); // 'list' | 'edit'
  const [editMachine, setEditMachine] = useState(null); // {id,name,code,...}

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadMachines = async () => {
    try {
      const res = await api.get("/machines");
      setMachines(res.data || []);
    } catch (e) {
      setMsg({ type: "danger", text: "Gagal memuat daftar mesin" });
    }
  };

  useEffect(() => {
    loadMachines();
    (async () => {
      try {
        const tRes = await api.get("/tools");
        setAllTools(tRes.data || []);
      } catch (e) {
        setMsg({ type: "danger", text: "Gagal memuat tools" });
      }
    })();
  }, []);

  const toolOptions = useMemo(
    () =>
      (allTools || []).map((t) => ({
        value: t.id,
        label: `${t.part_name}${t.kode ? " — " + t.kode : ""}${
          t.spec ? " (" + t.spec + ")" : ""
        }`,
        raw: t,
      })),
    [allTools],
  );

  // Opsi sektor (unik) dari daftar mesin.
  const sectorOptions = useMemo(() => {
    const map = new Map();
    (machines || []).forEach((m) => {
      const s = m.sector;
      if (s?.id != null && !map.has(s.id)) map.set(s.id, s.name || "-");
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      String(a.name).localeCompare(String(b.name)),
    );
  }, [machines]);

  const filteredMachines = useMemo(() => {
    let list = machines;
    if (sectorFilter) {
      list = list.filter((m) => String(m.sector?.id) === String(sectorFilter));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (m) =>
          m.name?.toLowerCase().includes(q) ||
          m.code?.toLowerCase().includes(q) ||
          m.sector?.name?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [machines, sectorFilter, search]);

  const openEditor = async (machine) => {
    setEditMachine(machine);
    setView("edit");
    setMsg(null);
    setLoading(true);
    try {
      const res = await api.get(`/machine-checksheet/${machine.id}`);
      setRows(
        (res.data.tools || []).map((t) => ({
          key: nextKey(),
          tool_id: t.tool_id,
          kode: t.kode,
          part_name: t.part_name,
          spec: t.spec,
          dimensi: t.dimensi || t.spec || "",
          checksheet_photo: t.checksheet_photo || null,
          standards: (t.standards || []).map((s) => ({
            key: nextKey(),
            item_check: s.item_check || "",
            frequency: s.frequency || "",
            type: s.type || "BIASA",
            max_usage: s.max_usage || "",
            requires_photo: !!s.requires_photo,
            photo: s.photo || null,
          })),
        })),
      );
    } catch (e) {
      setMsg({ type: "danger", text: "Gagal memuat check sheet mesin" });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const backToList = () => {
    setView("list");
    setEditMachine(null);
    setRows([]);
    setMsg(null);
    loadMachines(); // refresh jumlah tools di daftar
  };

  // ---- Tool ops ----
  const addTool = (opt) => {
    if (!opt) return;
    if (rows.some((r) => r.tool_id === opt.value)) {
      setMsg({ type: "warning", text: "Tool sudah ada di daftar" });
      return;
    }
    setRows((prev) => [
      ...prev,
      {
        key: nextKey(),
        tool_id: opt.value,
        kode: opt.raw.kode,
        part_name: opt.raw.part_name,
        spec: opt.raw.spec,
        dimensi: opt.raw.spec || "",
        checksheet_photo: null,
        standards: [],
      },
    ]);
  };

  // Upload foto Check Sheet (per tool): kompres (maks 1000px, JPEG 0.8) -> base64.
  const setToolPhoto = (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1000;
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setToolField(idx, "checksheet_photo", canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const removeTool = (idx) =>
    setRows((prev) => prev.filter((_, i) => i !== idx));

  const setToolField = (idx, field, value) =>
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    );

  // ---- Standard ops ----
  const addStandard = (ti) =>
    setRows((prev) =>
      prev.map((r, i) =>
        i === ti
          ? {
              ...r,
              standards: [
                ...r.standards,
                {
                  key: nextKey(),
                  item_check: "",
                  frequency: "H",
                  type: "BIASA",
                  max_usage: "",
                },
              ],
            }
          : r,
      ),
    );

  const removeStandard = (ti, si) =>
    setRows((prev) =>
      prev.map((r, i) =>
        i === ti
          ? { ...r, standards: r.standards.filter((_, j) => j !== si) }
          : r,
      ),
    );

  const setStdField = (ti, si, field, value) =>
    setRows((prev) =>
      prev.map((r, i) =>
        i === ti
          ? {
              ...r,
              standards: r.standards.map((s, j) =>
                j === si ? { ...s, [field]: value } : s,
              ),
            }
          : r,
      ),
    );

  // Catatan: upload foto per-standar DIHAPUS. Foto kini 1 per tool/jig dan
  // di-upload di halaman Tools (field foto pada data tool), bukan di standar.

  const handleSave = async () => {
    if (!editMachine) return;
    setSaving(true);
    setMsg(null);
    try {
      const payload = {
        tools: rows.map((r, i) => ({
          tool_id: r.tool_id,
          no_urut: i + 1,
          dimensi: r.dimensi || null,
          checksheet_photo: r.checksheet_photo ?? null,
          standards: r.standards
            .filter((s) => (s.item_check || "").trim() !== "")
            .map((s, j) => ({
              no_urut: j + 1,
              item_check: s.item_check.trim(),
              frequency: s.frequency || null,
              type: ["QTY", "QTY1", "QTY2", "STROKE", "NUMBER"].includes(s.type)
                ? s.type
                : "BIASA",
              max_usage:
                s.type === "STROKE" && String(s.max_usage ?? "").trim() !== ""
                  ? String(s.max_usage).trim()
                  : null,
            })),
        })),
      };
      await api.put(`/machine-checksheet/${editMachine.id}`, payload);
      // Kembali ke daftar mesin + tampilkan pesan sukses di sana
      setView("list");
      setEditMachine(null);
      setRows([]);
      await loadMachines();
      setMsg({ type: "success", text: "Check sheet berhasil disimpan" });
    } catch (e) {
      setMsg({
        type: "danger",
        text: e.response?.data?.message || "Gagal menyimpan check sheet",
      });
    } finally {
      setSaving(false);
    }
  };

  // ===================== LIST VIEW =====================
  if (view === "list") {
    return (
      <div className="cs-list-page" style={{ padding: 10 }}>
        <Card>
          <Card.Header
            style={{ background: "#1e3a8a", color: "#fff", fontWeight: 600 }}
          >
            Check Sheet Mesin — Daftar Mesin
          </Card.Header>
          <Card.Body>
            {msg && (
              <Alert
                variant={msg.type}
                onClose={() => setMsg(null)}
                dismissible
              >
                {msg.text}
              </Alert>
            )}
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
              <Form.Select
                value={sectorFilter}
                onChange={(e) => setSectorFilter(e.target.value)}
                style={{ maxWidth: 240 }}
              >
                <option value="">Semua Sektor</option>
                {sectorOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Form.Select>
              <InputGroup style={{ maxWidth: 420 }}>
                <Form.Control
                  placeholder="Cari mesin (nama / kode / sektor)..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
            </div>

            <Table striped bordered hover responsive className="align-middle">
              <thead>
                <tr style={{ background: "#1e3a8a", color: "#fff" }}>
                  <th style={{ width: 50 }}>No</th>
                  <th style={{ width: 110 }}>Kode</th>
                  <th>Nama Mesin</th>
                  <th style={{ width: 150 }}>Sektor</th>
                  <th style={{ width: 110 }}>Jml Tools</th>
                  <th style={{ width: 150 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredMachines.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted">
                      Tidak ada mesin
                    </td>
                  </tr>
                ) : (
                  filteredMachines.map((m, i) => (
                    <tr key={m.id}>
                      <td className="text-center">{i + 1}</td>
                      <td>{m.code || "-"}</td>
                      <td className="cell-left">{m.name || "-"}</td>
                      <td className="cell-left">{m.sector?.name || "-"}</td>
                      <td className="text-center">
                        <Badge bg={m.tools?.length ? "success" : "secondary"}>
                          {m.tools?.length || 0}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => openEditor(m)}
                        >
                          + Add Tools
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ===================== EDIT VIEW =====================
  return (
    <div style={{ padding: 10 }}>
      <Card className="mb-3">
        <Card.Header
          style={{ background: "#1e3a8a", color: "#fff", fontWeight: 600 }}
          className="d-flex justify-content-between align-items-center"
        >
          <span>
            Check Sheet — {editMachine?.name}{" "}
            {editMachine?.code && `(${editMachine.code})`}
          </span>
          <Button variant="light" size="sm" onClick={backToList}>
            ← Kembali ke Daftar
          </Button>
        </Card.Header>
        <Card.Body>
          {msg && (
            <Alert variant={msg.type} onClose={() => setMsg(null)} dismissible>
              {msg.text}
            </Alert>
          )}

          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" /> Memuat...
            </div>
          ) : (
            <>
              <Form.Label>Tambah Tool ke mesin ini</Form.Label>
              <Select
                options={toolOptions.filter(
                  (o) => !rows.some((r) => r.tool_id === o.value),
                )}
                value={null}
                onChange={addTool}
                placeholder="Cari & pilih tool untuk ditambahkan..."
                noOptionsMessage={() => "Tool tidak ditemukan"}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {!loading &&
        rows.map((r, ti) => (
          <Card className="mb-3" key={r.key}>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <Badge bg="secondary" className="me-2">
                  #{ti + 1}
                </Badge>
                <strong>{r.part_name}</strong>{" "}
                {r.kode && <span className="text-muted">— {r.kode}</span>}
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeTool(ti)}
              >
                Hapus Tool
              </Button>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3" style={{ maxWidth: 320 }}>
                <Form.Label>Dimensi</Form.Label>
                <Form.Control
                  type="text"
                  value={r.dimensi}
                  placeholder="mis. 3.4 * 5 * 1.0"
                  onChange={(e) => setToolField(ti, "dimensi", e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3" style={{ maxWidth: 320 }}>
                <Form.Label>Foto Check Sheet (kolom FOTO di lembar)</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  size="sm"
                  onChange={(e) => setToolPhoto(ti, e)}
                />
                <Form.Text className="text-muted">
                  Khusus Check Sheet, terpisah dari foto Pengecekan/Inspeksi.
                </Form.Text>
                {r.checksheet_photo && (
                  <div className="mt-2">
                    <img
                      src={r.checksheet_photo}
                      alt="Foto check sheet"
                      style={{
                        maxWidth: 160,
                        maxHeight: 120,
                        border: "1px solid #dee2e6",
                        borderRadius: 4,
                      }}
                    />
                    <div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="mt-1"
                        onClick={() => setToolField(ti, "checksheet_photo", null)}
                      >
                        Hapus Foto
                      </Button>
                    </div>
                  </div>
                )}
              </Form.Group>

              <Table bordered size="sm" responsive className="align-middle">
                <thead>
                  <tr style={{ background: "#f1f5f9" }}>
                    <th style={{ width: 40 }}>No</th>
                    <th>Item Check / Standar</th>
                    <th style={{ width: 130 }}>Frekuensi</th>
                    <th style={{ width: 130 }}>Tipe</th>
                    <th style={{ width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {r.standards.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted">
                        Belum ada standar. Klik "Tambah Baris Standar".
                      </td>
                    </tr>
                  ) : (
                    r.standards.map((s, si) => (
                      <tr key={s.key}>
                        <td className="text-center">{si + 1}</td>
                        <td>
                          <Form.Control
                            as="textarea"
                            rows={1}
                            value={s.item_check}
                            placeholder="mis. Visual OK tidak cacat / pecah"
                            onChange={(e) =>
                              setStdField(ti, si, "item_check", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <Form.Select
                            value={s.frequency}
                            onChange={(e) =>
                              setStdField(ti, si, "frequency", e.target.value)
                            }
                          >
                            <option value="">-</option>
                            {FREQ_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Select
                            value={s.type}
                            onChange={(e) =>
                              setStdField(ti, si, "type", e.target.value)
                            }
                          >
                            <option value="BIASA">Biasa</option>
                            <option value="NUMBER">Number</option>
                            <option value="QTY1">QTY Shift 1</option>
                            <option value="QTY2">QTY Shift 2</option>
                            <option value="QTY">QTY (Shift 1/2 — gabung)</option>
                            <option value="STROKE">Standar Stroke</option>
                          </Form.Select>
                        </td>
                        <td className="text-center">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeStandard(ti, si)}
                          >
                            ✕
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => addStandard(ti)}
              >
                + Tambah Baris Standar
              </Button>
            </Card.Body>
          </Card>
        ))}

      {!loading && (
        <div className="d-flex justify-content-end mb-4 gap-2">
          <Button variant="secondary" onClick={backToList}>
            Kembali
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" /> Menyimpan...
              </>
            ) : (
              "Simpan"
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ChecksheetTab;
